import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import toast from "react-hot-toast";
import { AppointmentService } from "../../../../services/api/appointment.service";
import { DetailedAppointment } from "../types";

interface RescheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  appointment: DetailedAppointment;
  reload: () => void;
}

export const RescheduleModal: React.FC<RescheduleModalProps> = ({
  isOpen,
  onClose,
  appointment,
  reload,
}) => {
  const [isRescheduling, setIsRescheduling] = useState(false);
  const [availableDates, setAvailableDates] = useState<string[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [availableSlots, setAvailableSlots] = useState<any[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<any>(null);
  const [loadingDates, setLoadingDates] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchDates();
    }
  }, [isOpen]);

  const fetchDates = async () => {
    setLoadingDates(true);
    setSelectedDate("");
    setAvailableSlots([]);
    setSelectedSlot(null);
    try {
      const response = await AppointmentService.getNextAvailableDates(
        appointment.doctorId,
        appointment.appointmentSettingId
      );
      if (response.success) {
        setAvailableDates(response.data || []);
      } else {
        toast.error(response.message || "Failed to fetch available dates");
      }
    } catch (error) {
      toast.error("Error fetching dates");
    } finally {
      setLoadingDates(false);
    }
  };

  const handleDateSelect = async (date: string) => {
    setSelectedDate(date);
    setLoadingSlots(true);
    setSelectedSlot(null);
    setAvailableSlots([]);
    try {
      const response = await AppointmentService.getDoctorSlots(
        date,
        appointment.appointmentSettingId,
        appointment.doctorId
      );
      if (response.success) {
        const slots = response.data || [];
        const now = new Date();
        const selectedDateObj = new Date(date);

        const filteredSlots = slots.filter((slot: any) => {
          if (
            now.getFullYear() === selectedDateObj.getFullYear() &&
            now.getMonth() === selectedDateObj.getMonth() &&
            now.getDate() === selectedDateObj.getDate()
          ) {
            const [hours, minutes] = slot.startTime.split(":");
            const slotTime = new Date(selectedDateObj);
            slotTime.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
            return slotTime > now;
          }
          return true;
        });

        setAvailableSlots(filteredSlots);
      } else {
        toast.error(response.message || "Failed to fetch slots");
      }
    } catch (error) {
      toast.error("Error fetching slots");
    } finally {
      setLoadingSlots(false);
    }
  };

  const submitReschedule = async () => {
    if (!selectedDate || !selectedSlot) {
      toast.error("Please select a date and time slot.");
      return;
    }

    const userStr = localStorage.getItem("auth_user");
    const user = JSON.parse(userStr || "{}");

    const payload = {
      clinicId: user.id,
      doctorId: appointment.doctorId,
      patientId: appointment.patientId,
      appointmentDate: selectedDate,
      startTime: selectedSlot.startTime,
      endTime: selectedSlot.endTime,
      appointmentId: appointment.id,
      consultationType: appointment.consultationType,
      appointmentSettingClinicId: selectedSlot.clinicId,
      appointmentBy: "clinic",
    };

    setIsRescheduling(true);
    try {
      const response = await AppointmentService.rescheduleAppointment(payload);
      if (response.success) {
        toast.success("Appointment rescheduled successfully");
        onClose();
        reload();
      } else {
        toast.error(response.message || "Failed to reschedule appointment");
      }
    } catch (error) {
      toast.error("Failed to reschedule");
    } finally {
      setIsRescheduling(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden border border-gray-100">
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100">
          <h3 className="text-base font-semibold text-gray-900">Reschedule Appointment</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:bg-gray-100 hover:text-gray-600 p-1.5 rounded-lg transition-colors"
            disabled={isRescheduling}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-6 flex-1 overflow-auto">
          {loadingDates ? (
            <div className="flex justify-center p-8 text-gray-500 text-sm">Loading available dates...</div>
          ) : (
            <div className="space-y-6">
              <div>
                <h4 className="font-semibold text-gray-800 text-sm mb-3">Select a Date</h4>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                  {availableDates.length === 0 && (
                    <p className="col-span-full text-xs text-gray-500">No dates available.</p>
                  )}
                  {availableDates.map((date) => (
                    <button
                      key={date}
                      onClick={() => handleDateSelect(date)}
                      className={`py-2 px-3 border rounded-xl font-medium text-xs transition-colors ${
                        selectedDate === date
                          ? "bg-emerald-600 border-emerald-600 text-white"
                          : "bg-white border-gray-200 text-gray-700 hover:border-emerald-500"
                      }`}
                    >
                      {new Date(date).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                    </button>
                  ))}
                </div>
              </div>

              {selectedDate && (
                <div>
                  <h4 className="font-semibold text-gray-800 text-sm mb-3">Select a Time Slot</h4>
                  {loadingSlots ? (
                    <div className="text-gray-500 text-xs">Loading slots...</div>
                  ) : (
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                      {availableSlots.length === 0 && (
                        <p className="col-span-full text-xs text-gray-500">No slots available for this date.</p>
                      )}
                      {availableSlots.map((slot) => (
                        <button
                          key={slot.id}
                          onClick={() => setSelectedSlot(slot)}
                          className={`py-2 px-2 border rounded-xl font-medium text-xs transition-colors ${
                            selectedSlot?.id === slot.id
                              ? "bg-emerald-600 border-emerald-600 text-white"
                              : "bg-white border-gray-200 text-gray-700 hover:border-emerald-500"
                          }`}
                        >
                          {slot.startTime}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex justify-end px-6 py-4 border-t border-gray-100 gap-3 bg-gray-50/50">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-200 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors"
            disabled={isRescheduling}
          >
            Cancel
          </button>
          <button
            onClick={submitReschedule}
            className="px-5 py-2 bg-emerald-600 rounded-lg text-xs font-semibold text-white hover:bg-emerald-700 transition-colors disabled:opacity-50"
            disabled={isRescheduling || !selectedSlot}
          >
            {isRescheduling ? "Rescheduling..." : "Confirm Reschedule"}
          </button>
        </div>
      </div>
    </div>
  );
};
